import { Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  CommodityType,
  CreateOfficeAirportChargeRequest,
  OfficeChargeCategory,
  OfficeChargeUnitOfMeasure,
  OfficeService,
} from '@cai-services';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  Currency,
  GlobalService,
  Office,
  OfficeAirportCharge,
} from '../../../../core';
import { FormUtil, SearchUtil } from '../../../../utils';
import {
  greaterThanControlValidator,
  isNumberValidator,
  requiredIfCheckboxCheckedValidator,
} from '../../../../validators';

@Component({
  selector: 'cai-office-add-airport-charge-modal',
  templateUrl: './office-add-airport-charge-modal.component.html',
  styleUrls: ['./office-add-airport-charge-modal.component.scss'],
  standalone: false,
})
export class OfficeAddAirportChargeModal implements OnInit {
  @Input()
  allCurrencies: Currency[];
  @Input()
  activeOffice: Office;
  @Input()
  officeAirportCharges: OfficeAirportCharge[];
  @Input()
  modalRef: NgbModalRef;

  addAirportChargeForm: FormGroup;

  airportSelectorOptions: any[];
  officeServiceTooltipError = '';
  officeChargeUnitOfMeasure = OfficeChargeUnitOfMeasure;
  officeChargeCategory = OfficeChargeCategory;
  commodityType = CommodityType;

  constructor(
    private readonly fb: FormBuilder,
    private readonly globalService: GlobalService,
    private readonly officeService: OfficeService,
  ) {}

  async ngOnInit() {
    this.addAirportChargeForm = this.fb.group({
      airportCode: ['', [Validators.required]],
      currencyCode: ['', [Validators.required]],
      firstCharge: this.fb.group({
        name: ['', [Validators.required]],
        markup: [
          '',
          [isNumberValidator(), Validators.min(0)],
        ],
        isMarkupFlat: [true],
        buyingRate: ['',  [
          isNumberValidator(),
          Validators.min(0)
        ]],
        sellingRate: ['', [
          Validators.required,
          isNumberValidator(),
          Validators.min(0)
        ]],
        markupMinFlat: ['', [isNumberValidator(), Validators.min(0)]],
        markupMaxFlat: [
          '',
          [
            isNumberValidator(),
            Validators.min(0),
            greaterThanControlValidator('markupMinFlat'),
          ],
        ],
        unitOfMeasure: ['', [Validators.required]],
        chargeCategory: ['EXPORT'],
        defaultCommodity: [
          { value: '', disabled: true },
          [requiredIfCheckboxCheckedValidator('isDefault')],
        ],
        isDefault: [false, [Validators.required]],
      }),
    });
    const airportsInOfficeCountry = await this.globalService.getAirports();
    this.airportSelectorOptions = SearchUtil.fillAirportAutocompleteOptions(
      airportsInOfficeCountry,
    );
    this.addAirportChargeForm
      .get('currencyCode')
      .setValue(this.activeOffice.country.defaultCurrency.currencyCode);
    this.addAirportChargeForm
      .get('firstCharge.isDefault')
      .valueChanges.subscribe((isDefault) => {
        if (isDefault) {
          this.addAirportChargeForm
            .get('firstCharge.defaultCommodity')
            .enable();
        } else {
          this.addAirportChargeForm
            .get('firstCharge.defaultCommodity')
            .setValue('');
          this.addAirportChargeForm
            .get('firstCharge.defaultCommodity')
            .disable();
        }
      });

      this.addAirportChargeForm.get('firstCharge.buyingRate').valueChanges.subscribe(_ => this.recalculateSellingRate());
      this.addAirportChargeForm.get('firstCharge.markup').valueChanges.subscribe(_ => this.recalculateSellingRate());
      this.addAirportChargeForm.get('firstCharge.isMarkupFlat').valueChanges.subscribe(_ => this.recalculateSellingRate());
  }

  private recalculateSellingRate(): void {
    const buyingRate = +this.addAirportChargeForm.get('firstCharge.buyingRate').value;
    const markup = +this.addAirportChargeForm.get('firstCharge.markup').value;
    const sellingRateControl = this.addAirportChargeForm.get('firstCharge.sellingRate');
    if (!(buyingRate > 0) || !(markup >= 0)) {
      sellingRateControl.enable();
      return;
    }

    const sellingRate = this.addAirportChargeForm.get('firstCharge.isMarkupFlat').value === true
      ? buyingRate + markup
      : buyingRate + (buyingRate * (markup / 100));
    sellingRateControl.setValue(sellingRate);
    sellingRateControl.disable();
  }

  async createOfficeAirportChargeFromForm(): Promise<void> {
    const markupValue = this.addAirportChargeForm.get('firstCharge.markup').value ?? 0;
    const isMarkupFlat = this.addAirportChargeForm.get('firstCharge.isMarkupFlat').value;
    const isDefault = this.addAirportChargeForm.get('firstCharge.isDefault').value;
    const buyingRateValue = this.addAirportChargeForm.get('firstCharge.buyingRate').value;
    const markupMinFlatValue = this.addAirportChargeForm.get('firstCharge.markupMinFlat').value;
    const markupMaxFlatValue = this.addAirportChargeForm.get('firstCharge.markupMaxFlat').value;

    const airportCharge: CreateOfficeAirportChargeRequest = {
      airportCode: this.addAirportChargeForm.get('airportCode').value,
      currencyCode: this.addAirportChargeForm.get('currencyCode').value,
      firstCharge: {
        name: this.addAirportChargeForm.get('firstCharge.name').value,
        markupFlatRate: isMarkupFlat ? markupValue : 0,
          markupPercent: isMarkupFlat ? 0 : markupValue / 100,
          buyingRate: buyingRateValue ? +buyingRateValue : undefined,
          sellingRate: +this.addAirportChargeForm.get('firstCharge.sellingRate').value,
          markupMinFlat: markupMinFlatValue ? +markupMinFlatValue : undefined,
          markupMaxFlat: markupMaxFlatValue ? +markupMaxFlatValue : undefined,
          unitOfMeasure: this.addAirportChargeForm.get('firstCharge.unitOfMeasure').value,
          isDefault,
          defaultCommodity: isDefault
            ? this.addAirportChargeForm.get('firstCharge.defaultCommodity').value
            : undefined,
          chargeCategory: this.addAirportChargeForm.get?.('firstCharge.chargeCategory')?.value || undefined,
      }
    };
    try {
      await this.officeService.createOfficeAirportCharge(
        this.activeOffice.id,
        airportCharge,
      );
    } catch (e) {
      console.error(e);
    }
    this.modalRef.close(true);
  }

  onSelectAirport({ value: airportCode }: any): void {
    this.addAirportChargeForm.get('airportCode').setValue(airportCode);
  }

  onHoverChargesField(controlName: string): void {
    this.officeServiceTooltipError = FormUtil.getFirstError(
      this.addAirportChargeForm.get('firstCharge') as FormGroup,
      controlName,
    );
  }

  get airportControl(): FormControl {
    return this.addAirportChargeForm?.get('airportCode') as FormControl;
  }
}
